import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Button } from 'antd';
import { StopOutlined } from '@ant-design/icons';
import { useSchedulingData, SchedView } from './useSchedulingData';
import { ScheduleControls } from './parts/ScheduleControls';
import { WeekGrid } from './views/WeekGrid';
import { DayGrid } from './views/DayGrid';
import { MobileWeek } from './views/MobileWeek';
import { MobileDay } from './views/MobileDay';
import { MonthView } from './views/MonthView';
import { AgendaView } from './views/AgendaView';
import { useApptDrag } from './dnd/useApptDrag';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useCapability, useHasRole } from '../auth/RoleGuard';
import { BlockTimeDrawer } from './BlockTimeDrawer';
import { ScheduleException } from '../../services/scheduleApi';
import { RequestsPanel } from './RequestsPanel';
import { CancelledAppointmentsView } from '../SchedulingHub/CancelledAppointmentsView';
import {
  AppointmentDetailModal,
  RescheduleModal,
  TechnicianChangeModal,
  CancelAppointmentModal,
} from '../Appointments';
import { Appointment } from '../../services/appointmentApi';

export function Scheduling() {
  // SP-1 Task 7: mobile defaults to Agenda; desktop keeps the pre-existing
  // default ('week'). useIsMobile reads window.innerWidth synchronously on
  // first render, so it's safe to consult inside the view state's lazy
  // initializer here (declared before the `view` state so it's in scope).
  const isMobile = useIsMobile();
  const [view, setView] = useState<SchedView>(() => (isMobile ? 'agenda' : 'week'));
  const [anchor, setAnchor] = useState(() => dayjs());
  const [selectedDate, setSelectedDate] = useState(() => dayjs());
  const [technicianId, setTechnicianId] = useState<number | undefined>(undefined);
  const [showCancelled, setShowCancelled] = useState(false);
  // owner/advisor OR explicit manage_schedule permission — mirrors the backend
  // requireScheduleWriteAccess policy (owner/advisor always + manage_schedule
  // capability) and SchedulingHub's gate for the Requests panel + Cancelled
  // toggle. Also gates the WF-1 availability-block create/edit entry points.
  const isOwnerOrAdvisor = useHasRole(['owner', 'advisor']);
  const hasManageScheduleCap = useCapability('manage_schedule');
  const canManageSchedule = isOwnerOrAdvisor || hasManageScheduleCap;
  // Shop-wide (holiday) blocks are owner/advisor only — mirrors the server's
  // scope='shop' guard in routes/schedules.ts.
  const canShopWide = useHasRole(['owner', 'advisor']);

  // MobileWeek's day strip is scoped to the anchor's week — whenever the week
  // itself changes (prev/next/today in ScheduleControls), snap the focused
  // day back to the anchor so the strip's active day is always in range.
  useEffect(() => {
    setSelectedDate(anchor);
  }, [anchor]);

  // Symmetric reconciliation: MobileWeek's swipe-between-days can move
  // selectedDate past the edge of the anchor's current week (swipe left on
  // the last day / right on the first). When that happens, roll the anchor
  // to follow so weekStart re-derives around the new selected day and the
  // strip stays in sync with the grid instead of showing a stale week.
  // Setting anchor here re-triggers the effect above with the same date, so
  // it converges in one extra render rather than looping.
  useEffect(() => {
    const weekStart = anchor.startOf('week');
    const weekEnd = weekStart.add(6, 'day');
    if (selectedDate.isBefore(weekStart, 'day') || selectedDate.isAfter(weekEnd, 'day')) {
      setAnchor(selectedDate);
    }
  }, [selectedDate]);

  const { appts, techs, exceptions, loading, error, reload } = useSchedulingData(view, anchor, technicianId);
  // `appts`/`techs` are passed purely so a drag can LOG what it moved and which
  // technician colour resolved before and after (WP-01) — the write itself is
  // driven entirely by the drop intent.
  const { sensors, onDragEnd } = useApptDrag(reload, { appts, techs });

  // Edit-modal state, mirroring SchedulingHub.tsx's modalAppointment + 4
  // visibility flags exactly, so behavior (which modal opens from which
  // action, what closes what) matches the legacy screen.
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [rescheduleModalVisible, setRescheduleModalVisible] = useState(false);
  const [techChangeModalVisible, setTechChangeModalVisible] = useState(false);
  const [cancelModalVisible, setCancelModalVisible] = useState(false);
  const [modalAppointment, setModalAppointment] = useState<Appointment | null>(null);

  // WF-1 Task 8: availability BlockTimeDrawer — create from the toolbar button
  // or an empty-slot tap, edit from tapping an existing block.
  const [blockDrawerOpen, setBlockDrawerOpen] = useState(false);
  const [editingException, setEditingException] = useState<ScheduleException | null>(null);
  const [blockDefaults, setBlockDefaults] = useState<{ techId?: number; start?: string; end?: string }>({});

  const openBlockCreate = () => {
    setEditingException(null);
    setBlockDefaults({});
    setBlockDrawerOpen(true);
  };

  const openBlockForSlot = (techId: number | undefined, startLocal: string) => {
    setEditingException(null);
    setBlockDefaults({
      techId,
      start: startLocal,
      end: dayjs(startLocal).add(1, 'hour').format('YYYY-MM-DD HH:mm:ss'),
    });
    setBlockDrawerOpen(true);
  };

  const openBlockEdit = (ex: ScheduleException) => {
    setEditingException(ex);
    setBlockDefaults({});
    setBlockDrawerOpen(true);
  };

  const handleBlockSaved = () => {
    setBlockDrawerOpen(false);
    setEditingException(null);
    reload();
  };

  const handleAppointmentClick = (appt: Appointment) => {
    setModalAppointment(appt);
    setDetailModalVisible(true);
  };

  // The grids only know the appointment id (they segment SegmentedAppointment
  // internally); look up the full record from the hook's appts so the modal
  // gets a real Appointment.
  const handleOpen = (id: number) => {
    const found = appts.find((a) => a.id === id);
    if (found) handleAppointmentClick(found);
  };

  const handleReschedule = (appt: Appointment) => {
    setModalAppointment(appt);
    setDetailModalVisible(false);
    setRescheduleModalVisible(true);
  };

  const handleChangeTechnician = (appt: Appointment) => {
    setModalAppointment(appt);
    setDetailModalVisible(false);
    setTechChangeModalVisible(true);
  };

  const handleCancel = (appt: Appointment) => {
    setModalAppointment(appt);
    setDetailModalVisible(false);
    setCancelModalVisible(true);
  };

  const handleModalSuccess = () => {
    setDetailModalVisible(false);
    setRescheduleModalVisible(false);
    setTechChangeModalVisible(false);
    setCancelModalVisible(false);
    setModalAppointment(null);
    reload();
  };

  const handleDetailModalClose = () => {
    setDetailModalVisible(false);
    setModalAppointment(null);
  };

  const handleRescheduleModalClose = () => {
    setRescheduleModalVisible(false);
    setModalAppointment(null);
  };

  const handleTechChangeModalClose = () => {
    setTechChangeModalVisible(false);
    setModalAppointment(null);
  };

  const handleCancelModalClose = () => {
    setCancelModalVisible(false);
    setModalAppointment(null);
  };

  if (showCancelled) {
    return <CancelledAppointmentsView onBack={() => setShowCancelled(false)} />;
  }

  return (
    <div data-testid="scheduling-tab">
      <ScheduleControls
        view={view}
        onView={setView}
        anchor={anchor}
        onAnchor={setAnchor}
        techs={techs}
        technicianId={technicianId}
        onTech={setTechnicianId}
        onBlockTime={canManageSchedule ? openBlockCreate : undefined}
      />
      {canManageSchedule && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', margin: '8px 0' }}>
          <Button icon={<StopOutlined />} onClick={() => setShowCancelled(true)}>
            Cancelled Appointments
          </Button>
        </div>
      )}
      {canManageSchedule && <RequestsPanel />}
      <div data-testid="scheduling-body">
        {loading && <div data-testid="scheduling-loading">Loading…</div>}
        {error && <div data-testid="scheduling-error">{error}</div>}
        {view === 'week' && isMobile ? (
          <MobileWeek
            weekStart={anchor.startOf('week')}
            selectedDate={selectedDate}
            onSelectDate={setSelectedDate}
            appts={appts}
            techs={techs}
            exceptions={exceptions}
            onOpen={handleOpen}
            onDrop={onDragEnd}
            sensors={sensors}
          />
        ) : view === 'week' && !isMobile ? (
          <WeekGrid
            weekStart={anchor.startOf('week')}
            appts={appts}
            techs={techs}
            exceptions={exceptions}
            onOpen={handleOpen}
            onDrop={onDragEnd}
            sensors={sensors}
            onEditBlock={canManageSchedule ? openBlockEdit : undefined}
            onCreateBlock={canManageSchedule ? openBlockForSlot : undefined}
          />
        ) : view === 'day' && isMobile ? (
          <MobileDay
            date={anchor}
            appts={appts}
            techs={techs}
            exceptions={exceptions}
            onOpen={handleOpen}
            onDrop={onDragEnd}
            sensors={sensors}
            onEditBlock={canManageSchedule ? openBlockEdit : undefined}
            onCreateBlock={canManageSchedule ? openBlockForSlot : undefined}
          />
        ) : view === 'day' && !isMobile ? (
          <DayGrid
            date={anchor}
            appts={appts}
            techs={techs}
            exceptions={exceptions}
            onOpen={handleOpen}
            onDrop={onDragEnd}
            sensors={sensors}
            onEditBlock={canManageSchedule ? openBlockEdit : undefined}
            onCreateBlock={canManageSchedule ? openBlockForSlot : undefined}
          />
        ) : view === 'month' ? (
          <MonthView
            anchor={anchor}
            appts={appts}
            exceptions={exceptions}
            onSelectDate={(d) => {
              setAnchor(d);
              setView('day');
            }}
          />
        ) : view === 'agenda' ? (
          <AgendaView
            date={anchor}
            appts={appts}
            techs={techs}
            exceptions={exceptions}
            onOpen={handleOpen}
          />
        ) : (
          <div data-testid={`view-${view}`} data-appt-count={appts.length} data-exception-count={exceptions.length} />
        )}
      </div>

      <AppointmentDetailModal
        key={modalAppointment?.id || 'empty'}
        visible={detailModalVisible}
        appointment={modalAppointment}
        onClose={handleDetailModalClose}
        onReschedule={handleReschedule}
        onChangeTechnician={handleChangeTechnician}
        onCancel={handleCancel}
      />

      <RescheduleModal
        visible={rescheduleModalVisible}
        appointment={modalAppointment}
        onClose={handleRescheduleModalClose}
        onSuccess={handleModalSuccess}
      />

      <TechnicianChangeModal
        visible={techChangeModalVisible}
        appointment={modalAppointment}
        onClose={handleTechChangeModalClose}
        onSuccess={handleModalSuccess}
      />

      <CancelAppointmentModal
        visible={cancelModalVisible}
        appointment={modalAppointment}
        onClose={handleCancelModalClose}
        onSuccess={handleModalSuccess}
      />

      {canManageSchedule && (
        <BlockTimeDrawer
          open={blockDrawerOpen}
          onClose={() => setBlockDrawerOpen(false)}
          onSaved={handleBlockSaved}
          techs={techs.map((t) => ({ id: t.id, name: t.name }))}
          defaultTechId={blockDefaults.techId}
          defaultStart={blockDefaults.start}
          defaultEnd={blockDefaults.end}
          editing={editingException}
          canShopWide={canShopWide}
        />
      )}
    </div>
  );
}

export default Scheduling;
